import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Gamepad2, ShieldCheck, FileText, Info, ShoppingBag, Home, Briefcase } from "lucide-react";

const LINKS = [
  { path: "/games",      label: "Играть",      icon: Home,        accent: "#ff3cac" },
  { path: "/news",       label: "Блог",        icon: FileText,    accent: "#3dffa0" },
  { path: "/how-to-play", label: "Как играть", icon: Info,        accent: "#00f5ff" },
  { path: "/pro",        label: "Купить PRO",  icon: ShoppingBag, accent: "#9b5de5" },
  { path: "/business",   label: "Для бизнеса", icon: Briefcase,   accent: "#ffe600" },
];

const VK_URL = "https://vk.com/neonparty_ru";

function VkIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.391 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4 8.408 4 7.932c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.677.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.204.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.169.508.271.508.22 0 .407-.136.813-.542 1.253-1.405 2.151-3.574 2.151-3.574.119-.254.322-.491.762-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.202 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.745-.576.745z"/>
    </svg>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 999,
        background: scrolled ? "rgba(10,3,26,0.97)" : "rgba(10,3,26,0.85)",
        backdropFilter: "blur(12px)",
        borderBottom: scrolled ? "2px solid rgba(255,60,172,0.3)" : "2px solid rgba(255,255,255,0.05)",
        transition: "all 0.25s ease",
        boxShadow: scrolled ? "0 4px 30px rgba(0,0,0,0.5)" : "none",
      }}>
        <div style={{
          maxWidth: "1400px", margin: "0 auto",
          padding: "0 1.25rem",
          height: "3.5rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              background: "none", border: "none", cursor: "pointer",
              padding: "0.25rem 0.5rem",
              borderRadius: "0.5rem",
              transition: "transform 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            <div style={{
              background: "#ff3cac", border: "2px solid #000",
              borderRadius: "0.5rem", boxShadow: "2px 2px 0 #000",
              width: "1.75rem", height: "1.75rem",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Gamepad2 style={{ width: "1rem", height: "1rem", color: "#fff" }} />
            </div>
            <span style={{
              fontFamily: "'Fredoka One', sans-serif",
              fontSize: "1.1rem", color: "#fff", letterSpacing: "0.05em",
            }}>NEONPARTY</span>
          </button>

          {/* Desktop links */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
               className="navbar-desktop-links">
            {LINKS.map(({ path, label, icon: Icon, accent }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                style={{
                  display: "flex", alignItems: "center", gap: "0.35rem",
                  background: isActive(path) ? `${accent}22` : "none",
                  border: isActive(path) ? `1.5px solid ${accent}66` : "1.5px solid transparent",
                  borderRadius: "0.6rem",
                  padding: "0.35rem 0.75rem",
                  cursor: "pointer",
                  color: isActive(path) ? accent : "#bbb",
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => {
                  if (!isActive(path)) {
                    e.currentTarget.style.color = accent;
                    e.currentTarget.style.background = `${accent}11`;
                    e.currentTarget.style.borderColor = `${accent}44`;
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive(path)) {
                    e.currentTarget.style.color = "#bbb";
                    e.currentTarget.style.background = "none";
                    e.currentTarget.style.borderColor = "transparent";
                  }
                }}
              >
                <Icon style={{ width: "0.85rem", height: "0.85rem" }} />
                {label}
              </button>
            ))}

            {/* ВКонтакте */}
            <a
              href={VK_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: "0.35rem",
                background: "none",
                border: "1.5px solid transparent",
                borderRadius: "0.6rem",
                padding: "0.35rem 0.75rem",
                cursor: "pointer",
                color: "#bbb",
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700,
                fontSize: "0.85rem",
                transition: "all 0.15s",
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = "#4c80c4";
                e.currentTarget.style.background = "#4c80c411";
                e.currentTarget.style.borderColor = "#4c80c444";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = "#bbb";
                e.currentTarget.style.background = "none";
                e.currentTarget.style.borderColor = "transparent";
              }}
            >
              <VkIcon size={14} />
              ВКонтакте
            </a>
          </div>

          {/* Mobile burger */}
          <button
            className="navbar-burger"
            onClick={() => setOpen(v => !v)}
            style={{
              background: open ? "rgba(255,60,172,0.15)" : "rgba(255,255,255,0.05)",
              border: `1.5px solid ${open ? "#ff3cac55" : "rgba(255,255,255,0.1)"}`,
              borderRadius: "0.6rem",
              padding: "0.4rem",
              cursor: "pointer",
              color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
          >
            {open
              ? <X style={{ width: "1.25rem", height: "1.25rem" }} />
              : <Menu style={{ width: "1.25rem", height: "1.25rem" }} />
            }
          </button>
        </div>

        {/* Mobile dropdown */}
        <div style={{
          maxHeight: open ? "400px" : "0",
          overflow: "hidden",
          transition: "max-height 0.3s ease",
          borderTop: open ? "1px solid rgba(255,255,255,0.07)" : "none",
        }}>
          <div style={{ padding: "0.5rem 1rem 1rem" }}>
            {LINKS.map(({ path, label, icon: Icon, accent }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "0.75rem",
                  background: isActive(path) ? `${accent}18` : "transparent",
                  border: "none", borderRadius: "0.75rem",
                  padding: "0.75rem 1rem", cursor: "pointer",
                  color: isActive(path) ? accent : "#ccc",
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: 700, fontSize: "1rem",
                  textAlign: "left",
                  borderLeft: isActive(path) ? `3px solid ${accent}` : "3px solid transparent",
                  transition: "all 0.15s",
                  marginBottom: "0.2rem",
                }}
              >
                <Icon style={{ width: "1.1rem", height: "1.1rem", color: accent, flexShrink: 0 }} />
                {label}
              </button>
            ))}
            {/* ВКонтакте */}
            <a
              href={VK_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "0.75rem",
                background: "transparent",
                borderRadius: "0.75rem",
                padding: "0.75rem 1rem", cursor: "pointer",
                color: "#ccc",
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700, fontSize: "1rem",
                textAlign: "left",
                borderLeft: "3px solid transparent",
                textDecoration: "none",
                marginBottom: "0.2rem",
                boxSizing: "border-box",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = "#4c80c4";
                e.currentTarget.style.background = "#4c80c418";
                e.currentTarget.style.borderLeft = "3px solid #4c80c4";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = "#ccc";
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderLeft = "3px solid transparent";
              }}
            >
              <VkIcon size={18} />
              ВКонтакте
            </a>
          </div>
        </div>
      </nav>

      {/* Spacer so content doesn't hide under navbar */}
      <div style={{ height: "3.5rem" }} />

      <style>{`
        .navbar-burger { display: none !important; }
        .navbar-desktop-links { display: flex !important; }
        @media (max-width: 768px) {
          .navbar-burger { display: flex !important; }
          .navbar-desktop-links { display: none !important; }
        }
      `}</style>
    </>
  );
}